import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {derivedThemeAtom} from 'atoms/layout';
import {AdminHomeBannersService, type HomeBannerEligible, HomeBannersService} from 'client';
import {isContentEditor} from 'helpers/utils';
import {useAtom, useAtomValue} from 'jotai';
import {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router';
import {getBackendUrl, getService} from 'utilities';

import {bannerDismissedAtom} from './bannerDismissed';
import {ManagedBanner, ManagedBannerContent} from './ManagedBanner';
import {isBannerId, pickManagedBanner, pruneDismissedBanners} from './managedBannerSelection';

type BannerWithImages = ManagedBannerContent & {
  image_path?: string | null;
  dark_image_path?: string | null;
};

const imageUrl = (path?: string | null) => (path ? `${getBackendUrl()}${path}` : undefined);

const ShownBanner = ({banner, onDismiss}: {banner: BannerWithImages; onDismiss: () => void}) => {
  const theme = useAtomValue(derivedThemeAtom);
  return (
    <ManagedBanner
      banner={banner}
      mode={theme === 'dark' ? 'dark' : 'bright'}
      imageSrc={imageUrl(banner.image_path)}
      darkImageSrc={imageUrl(banner.dark_image_path)}
      onDismiss={onDismiss}
    />
  );
};

/**
 * The shortest banner live for the viewer that they haven't dismissed. It's
 * chosen once per visit: dismissing it hides it, and the next one shows on the
 * next page load.
 */
const EligibleBanner = () => {
  const [dismissed, setDismissed] = useAtom(bannerDismissedAtom);
  const [timezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  // Undefined until the eligible banners load; null when none is left to show.
  const [shown, setShown] = useState<HomeBannerEligible | null>();

  const {data} = useQuery({
    queryKey: ['home', 'banners', 'eligible', timezone],
    queryFn: () =>
      getService(HomeBannersService).listEligibleHomeBannersApiV1HomeBannersEligibleGet(timezone),
  });

  useEffect(() => {
    if (!data || shown !== undefined) {
      return;
    }
    const pruned = pruneDismissedBanners(
      dismissed,
      data.map((banner) => banner.id)
    );
    if (pruned !== dismissed) {
      setDismissed(pruned);
    }
    setShown(pickManagedBanner(data, pruned) ?? null);
  }, [data, dismissed, setDismissed, shown]);

  if (!shown || dismissed[shown.id]) {
    return null;
  }
  return (
    <ShownBanner
      banner={shown}
      onDismiss={() => setDismissed((prev) => ({...prev, [shown.id]: true}))}
    />
  );
};

/**
 * `?banner={id}` for a content editor: that banner whatever its schedule, so it
 * can be checked on home before it goes live. Dismissing it only hides it here.
 */
const PreviewBanner = ({id}: {id: string}) => {
  const [hidden, setHidden] = useState(false);
  const {data} = useQuery({
    queryKey: ['home', 'banners', 'preview', id],
    queryFn: () =>
      getService(AdminHomeBannersService).getAdminHomeBannerApiV1AdminHomeBannersBannerIdGet(id),
  });

  if (!data || hidden) {
    return null;
  }
  return <ShownBanner banner={data} onDismiss={() => setHidden(true)} />;
};

/** The home banner from the banners admins manage at /admin/home-banners. */
export const HomeManagedBanner = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('banner');
  // Anyone else gets the usual banner; the admin API would refuse them anyway.
  const previewId =
    requested && isBannerId(requested) && isContentEditor(pulseUser) ? requested : undefined;

  return previewId ? <PreviewBanner key={previewId} id={previewId} /> : <EligibleBanner />;
};
